import {
  CORE_SCHEMA_VERSION,
  type AutomationReceipt,
  type Capture,
  type ContextRecord,
  type ContextRelation,
  type CoreReference,
  type ReviewItem,
} from '../types/core'
import {
  captureInterpretationProposalSchema,
  captureInterpretationResponseSchema,
  captureInterpretationUsageResponseSchema,
  type CaptureInterpretationProposal,
  type CaptureInterpretationResponse,
} from '../types/captureInterpretation'
import { auth } from '../utils/firebase'
import type { CaptureAutomationWrite } from './coreDataService'

const MODEL_PROCESSOR = 'core-capture-model'
const MODEL_PROCESSING_VERSION = '1'
const SAFE_CONTEXT_CONFIDENCE = 0.84
const SAFE_RELATION_CONFIDENCE = 0.88
const SAFE_EXISTING_MATCH_CONFIDENCE = 0.96
const SAFE_NEW_PROFILE_CONFIDENCE = 0.94
const MIN_ESCALATION_WORDS = 7
const INTERPRETATION_TIMEOUT_MS = 8_000
const PROFILE_KINDS = new Set(['person', 'place', 'organization', 'project', 'topic', 'routine', 'obligation', 'reference'])

export type ModelFallbackOutcome = 'no_change' | 'rejected' | 'unavailable'
export type ModelFallbackCode = 'timeout' | 'response_contract' | 'service' | 'proposal_validation' | 'unknown'

const captureRef = (capture: Capture): CoreReference => ({ kind: 'capture', id: capture.id })
const clip = (value: string, length = 110) => value.length > length
  ? `${value.slice(0, length - 1).trimEnd()}…`
  : value
const normalized = (value: string) => value
  .replace(/[^\p{L}\p{N}\s]/gu, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLocaleLowerCase()
const evidenceAppearsIn = (evidence: string, source: string) => {
  const normalizedEvidence = normalized(evidence)
  return Boolean(normalizedEvidence) && normalized(source).includes(normalizedEvidence)
}

const linkEvidenceFor = (capture: Capture): string[] => capture.inputs.reduce<string[]>((evidence, input) => {
  if (input.kind === 'link') evidence.push('Link attachment', input.url)
  return evidence
}, [])

export const shouldEscalateCaptureInterpretation = (
  capture: Capture,
  deterministic: CaptureAutomationWrite,
): boolean => {
  if (deterministic.actions?.length || deterministic.reviewItems?.length) return false
  const primaryContext = deterministic.contextRecords?.[0]
  if (!primaryContext || primaryContext.form !== 'observation' || primaryContext.kind !== 'episode') return false

  const text = capture.primaryText?.trim() ?? ''
  const hasImage = capture.inputs.some((input) => input.kind === 'image')
  const hasLink = capture.inputs.some((input) => input.kind === 'link')
  return hasImage || hasLink || text.split(/\s+/).filter(Boolean).length >= MIN_ESCALATION_WORDS
}

export const annotateModelFallback = (
  deterministic: CaptureAutomationWrite,
  outcome: ModelFallbackOutcome,
  code?: ModelFallbackCode,
): CaptureAutomationWrite => {
  const modelAttempt = {
    processor: MODEL_PROCESSOR,
    outcome,
    ...(code ? { code } : {}),
  }
  const reason = outcome === 'no_change'
    ? 'Tydy\'s model checked the capture and kept the simpler interpretation.'
    : outcome === 'rejected'
      ? 'The model suggestion did not clear Tydy\'s confidence checks, so the simpler interpretation was kept.'
      : 'The model was unavailable, so Tydy kept the capture safely with the simpler interpretation.'

  return {
    ...deterministic,
    contextRecords: deterministic.contextRecords?.map((record) => ({
      ...record,
      derivation: { ...record.derivation, modelAttempt },
    })),
    contextRelations: deterministic.contextRelations?.map((relation) => ({
      ...relation,
      derivation: { ...relation.derivation, modelAttempt },
    })),
    receipt: {
      ...deterministic.receipt,
      reason,
      derivation: { ...deterministic.receipt.derivation, modelAttempt },
    },
  }
}

const validateProposalEvidence = (
  capture: Capture,
  proposal: CaptureInterpretationProposal,
  existingRecords: ContextRecord[],
) => {
  const source = [
    capture.primaryText?.trim(),
    capture.inputs.some((input) => input.kind === 'image') ? 'Image attachment' : '',
    ...linkEvidenceFor(capture),
  ].filter(Boolean).join('\n')
  if (!source) throw new Error('Model interpretation requires capture evidence.')

  const tempIds = new Set<string>()
  const existingById = new Map(existingRecords.map((record) => [record.id, record]))

  proposal.candidates.forEach((candidate) => {
    if (tempIds.has(candidate.tempId)) throw new Error('Model proposal contains duplicate candidate IDs.')
    tempIds.add(candidate.tempId)
    if (!evidenceAppearsIn(candidate.evidenceQuote, source)) {
      throw new Error('Model candidate evidence is not present in the capture.')
    }
    if (candidate.existingRecordId) {
      const existing = existingById.get(candidate.existingRecordId)
      if (!existing || existing.form !== candidate.form || existing.kind !== candidate.kind) {
        throw new Error('Model proposal referenced an unavailable context record.')
      }
    }
    if (candidate.observedAt && Number.isNaN(new Date(candidate.observedAt).getTime())) {
      throw new Error('Model proposal contains invalid observation timing.')
    }
    if (candidate.form !== 'observation' && candidate.observedAt) {
      throw new Error('Model proposal assigned observation timing to a non-observation record.')
    }
    if (candidate.form === 'profile' && !PROFILE_KINDS.has(candidate.kind)) {
      throw new Error('Model proposal used a profile for non-subject context.')
    }
  })

  proposal.relations.forEach((relation) => {
    if (!tempIds.has(relation.fromTempId) || !tempIds.has(relation.toTempId)) {
      throw new Error('Model relation references an unavailable candidate.')
    }
    if (!evidenceAppearsIn(relation.evidenceQuote, source)) {
      throw new Error('Model relation evidence is not present in the capture.')
    }
  })

  if (proposal.action && !evidenceAppearsIn(proposal.action.evidenceQuote, source)) {
    throw new Error('Model action evidence is not present in the capture.')
  }
}

const needsReview = (proposal: CaptureInterpretationProposal) => (
  Boolean(proposal.action)
  || proposal.confidence < SAFE_CONTEXT_CONFIDENCE
  || proposal.candidates.some((candidate) => (
    candidate.confidence < SAFE_CONTEXT_CONFIDENCE
    || (!candidate.existingRecordId && candidate.form === 'profile' && candidate.confidence < SAFE_NEW_PROFILE_CONFIDENCE)
    || (Boolean(candidate.existingRecordId) && candidate.confidence < SAFE_EXISTING_MATCH_CONFIDENCE)
  ))
  || proposal.relations.some((relation) => relation.confidence < SAFE_RELATION_CONFIDENCE)
)

const modelDerivation = (capture: Capture, confidence: number) => ({
  method: 'hybrid' as const,
  processor: MODEL_PROCESSOR,
  version: MODEL_PROCESSING_VERSION,
  confidence,
  sourceRefs: [captureRef(capture)],
})

const buildReviewAutomation = (
  capture: Capture,
  deterministic: CaptureAutomationWrite,
  proposal: CaptureInterpretationProposal,
  now: string,
): CaptureAutomationWrite => {
  const sourceRef = captureRef(capture)
  const reviewId = `review-model-${capture.id}`
  const receiptId = `receipt-${capture.id}`
  const recommendsAction = Boolean(proposal.action)
  const reviewItem: ReviewItem = {
    schemaVersion: CORE_SCHEMA_VERSION,
    id: reviewId,
    userId: capture.userId,
    type: recommendsAction ? 'consequence' : 'interpretation',
    status: 'pending',
    title: clip(proposal.action?.title ?? proposal.candidates[0]?.title ?? capture.primaryText ?? 'Review capture'),
    promptKey: recommendsAction
      ? 'core.review.modelActionPrompt'
      : 'core.review.modelInterpretationPrompt',
    subjectRefs: [sourceRef],
    evidenceRefs: [sourceRef],
    recommendedChoiceId: 'keep_context',
    choices: recommendsAction ? [
      { id: 'create_action', labelKey: 'core.review.choices.createAction', operation: 'apply' },
      { id: 'keep_context', labelKey: 'core.review.choices.keepContext', operation: 'confirm' },
      { id: 'dismiss', labelKey: 'core.review.choices.dismiss', operation: 'dismiss' },
    ] : [
      { id: 'keep_context', labelKey: 'core.review.choices.keepContext', operation: 'confirm' },
      { id: 'dismiss', labelKey: 'core.review.choices.dismiss', operation: 'dismiss' },
    ],
    receiptId,
    createdAt: now,
    updatedAt: now,
  }

  const deterministicContext = deterministic.contextRecords ?? []
  const deterministicRelations = deterministic.contextRelations ?? []
  const outputRefs = [
    ...deterministicContext.map((record): CoreReference => ({ kind: 'context', id: record.id })),
    ...deterministicRelations.map((relation): CoreReference => ({ kind: 'context_relation', id: relation.id })),
    { kind: 'review' as const, id: reviewId },
  ]
  const receipt: AutomationReceipt = {
    schemaVersion: CORE_SCHEMA_VERSION,
    id: receiptId,
    userId: capture.userId,
    status: 'needs_review',
    operation: 'interpret_capture',
    summary: recommendsAction
      ? 'Kept the context and held a possible action for review.'
      : 'Kept the context and held an uncertain interpretation for review.',
    reason: proposal.reason,
    inputRefs: [sourceRef],
    outputRefs,
    changes: [
      ...deterministicContext.map((record) => ({
        operation: 'create' as const,
        targetRef: { kind: 'context' as const, id: record.id },
      })),
      ...deterministicRelations.map((relation) => ({
        operation: 'link' as const,
        targetRef: { kind: 'context_relation' as const, id: relation.id },
      })),
      { operation: 'create', targetRef: { kind: 'review', id: reviewId } },
    ],
    derivation: modelDerivation(capture, proposal.confidence),
    idempotencyKey: `${capture.id}:interpret`,
    reversible: true,
    reviewItemId: reviewId,
    createdAt: now,
    updatedAt: now,
  }

  return {
    expectedCaptureUpdatedAt: capture.updatedAt,
    capture: {
      ...capture,
      status: 'needs_review',
      processingVersion: MODEL_PROCESSING_VERSION,
      outputRefs,
      processedAt: now,
      updatedAt: now,
    },
    contextRecords: deterministicContext,
    contextRelations: deterministicRelations,
    reviewItems: [reviewItem],
    receipt,
  }
}

export const buildModelCaptureAutomation = (
  capture: Capture,
  deterministic: CaptureAutomationWrite,
  rawProposal: CaptureInterpretationProposal,
  existingRecords: ContextRecord[],
  now = new Date().toISOString(),
): CaptureAutomationWrite => {
  const proposal = captureInterpretationProposalSchema.parse(rawProposal)
  validateProposalEvidence(capture, proposal, existingRecords)

  if (proposal.intent === 'no_change' || proposal.candidates.length === 0) return deterministic
  if (needsReview(proposal)) return buildReviewAutomation(capture, deterministic, proposal, now)

  const sourceRef = captureRef(capture)
  const candidateRefs = new Map<string, CoreReference>()
  const newContextRecords: ContextRecord[] = []
  const observationHasCommentary = proposal.candidates.some((candidate) => (
    !candidate.existingRecordId
    && candidate.form === 'observation'
    && Boolean(candidate.tydyCommentary)
  ))

  proposal.candidates.forEach((candidate, index) => {
    if (candidate.existingRecordId) {
      candidateRefs.set(candidate.tempId, { kind: 'context', id: candidate.existingRecordId })
      return
    }

    const id = `model-context-${capture.id}-${index}`
    const tydyCommentary = candidate.form === 'profile' && observationHasCommentary
      ? null
      : candidate.tydyCommentary
    candidateRefs.set(candidate.tempId, { kind: 'context', id })
    newContextRecords.push({
      schemaVersion: CORE_SCHEMA_VERSION,
      id,
      userId: capture.userId,
      form: candidate.form,
      kind: candidate.kind,
      status: 'active',
      confirmation: 'inferred',
      title: clip(candidate.title),
      ...(candidate.summary ? { summary: candidate.summary } : {}),
      ...(tydyCommentary ? { tydyCommentary: clip(tydyCommentary, 280) } : {}),
      facts: [],
      ...(candidate.form === 'observation' ? {
        observation: { observedAt: candidate.observedAt ?? capture.createdAt },
      } : {}),
      memberRefs: [],
      sourceRefs: [sourceRef],
      relatedRefs: [],
      derivation: modelDerivation(capture, candidate.confidence),
      createdAt: now,
      updatedAt: now,
    })
  })

  if (!newContextRecords.some((record) => record.form !== 'profile')) return deterministic

  const relations: ContextRelation[] = proposal.relations.map((relation, index) => ({
    schemaVersion: CORE_SCHEMA_VERSION,
    id: `model-relation-${capture.id}-${index}`,
    userId: capture.userId,
    status: 'active',
    confirmation: 'inferred',
    relation: relation.relation,
    fromRef: candidateRefs.get(relation.fromTempId) as { kind: 'context'; id: string },
    toRef: candidateRefs.get(relation.toTempId) as { kind: 'context'; id: string },
    evidenceRefs: [sourceRef],
    derivation: modelDerivation(capture, relation.confidence),
    createdAt: now,
    updatedAt: now,
  }))
  const outputRefs: CoreReference[] = [
    ...newContextRecords.map((record) => ({ kind: 'context' as const, id: record.id })),
    ...relations.map((relation) => ({ kind: 'context_relation' as const, id: relation.id })),
  ]
  const receipt: AutomationReceipt = {
    schemaVersion: CORE_SCHEMA_VERSION,
    id: `receipt-${capture.id}`,
    userId: capture.userId,
    status: 'applied',
    operation: 'enhance_capture',
    summary: `Organized the capture as ${newContextRecords.map((record) => record.title).join(', ')}.`,
    reason: proposal.reason,
    inputRefs: [sourceRef],
    outputRefs,
    changes: [
      ...newContextRecords.map((record) => ({
        operation: 'create' as const,
        targetRef: { kind: 'context' as const, id: record.id },
      })),
      ...relations.map((relation) => ({
        operation: 'link' as const,
        targetRef: { kind: 'context_relation' as const, id: relation.id },
      })),
    ],
    derivation: modelDerivation(capture, proposal.confidence),
    idempotencyKey: `${capture.id}:interpret`,
    reversible: true,
    createdAt: now,
    updatedAt: now,
  }

  return {
    expectedCaptureUpdatedAt: capture.updatedAt,
    capture: {
      ...capture,
      status: 'processed',
      processingVersion: MODEL_PROCESSING_VERSION,
      outputRefs,
      processedAt: now,
      updatedAt: now,
    },
    contextRecords: newContextRecords,
    contextRelations: relations,
    receipt,
  }
}

const withTimeout = <T>(operation: Promise<T>, timeoutMs: number, message: string): Promise<T> => new Promise((resolve, reject) => {
  const timeoutId = globalThis.setTimeout(() => reject(new Error(message)), timeoutMs)
  operation.then(
    (value) => {
      globalThis.clearTimeout(timeoutId)
      resolve(value)
    },
    (error) => {
      globalThis.clearTimeout(timeoutId)
      reject(error)
    },
  )
})

const fetchInterpretation = async (captureId: string): Promise<CaptureInterpretationResponse> => withTimeout((async () => {
  const user = auth.currentUser
  if (!user) throw new Error('Authentication is required for enhanced interpretation.')
  const token = await user.getIdToken()
  const response = await fetch('/api/capture/interpret', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ captureId }),
    signal: AbortSignal.timeout(INTERPRETATION_TIMEOUT_MS),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(error.error ?? 'Enhanced interpretation is unavailable.')
  }

  return captureInterpretationResponseSchema.parse(await response.json())
})(), INTERPRETATION_TIMEOUT_MS, 'Enhanced interpretation timed out.')

const fetchUsage = async () => {
  const user = auth.currentUser
  if (!user) throw new Error('Authentication is required for enhanced interpretation.')
  const token = await user.getIdToken()
  const response = await fetch('/api/capture/interpret/usage', {
    headers: { Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(8_000),
  })
  if (!response.ok) throw new Error('Enhanced interpretation usage is unavailable.')
  return captureInterpretationUsageResponseSchema.parse(await response.json()).usage
}

export const coreModelInterpretationService = {
  interpretCapture: fetchInterpretation,
  getUsage: fetchUsage,
}
